/**
 * The executive's "Ask for review…" box, and the text box with its n/400 counter that the admin's Reply and
 * Mark done dialogs reuse. The counter counts characters of the cleaned text, exactly as the Worker does.
 */
import { useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cleanReviewText, REVIEW_TEXT_MAX, reviewTextIssue } from '@saledeed/shared';
import { Button, ErrorBox, Modal } from '../components/ui';
import { askReview, myReviewsKey } from './api';

/** What the counter shows and whether the text may be sent (a blank box: not sendable, but not red either). */
export function reviewTextState(value: string) {
  const count = [...cleanReviewText(value)].length;
  const blank = count === 0;
  const issue = blank ? null : reviewTextIssue(value);
  return { count, blank, issue, sendable: !blank && issue === null };
}

export function ReviewTextBox({ label, hint, value, onChange, rows = 5 }: {
  label: string; hint?: string; value: string; onChange: (v: string) => void; rows?: number;
}) {
  const id = useId();
  const { count, issue } = reviewTextState(value);
  const bad = issue !== null;
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-sm font-medium text-slate-800">{label}</label>
      {hint && <p id={`${id}-hint`} className="text-xs text-slate-500">{hint}</p>}
      <textarea id={id} rows={rows} value={value} onChange={e => onChange(e.target.value)}
        aria-describedby={`${hint ? `${id}-hint ` : ''}${id}-count`} aria-invalid={bad || undefined}
        className={`rounded border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 ${bad ? 'border-red-600 focus:ring-red-600' : 'border-slate-300 focus:border-blue-600 focus:ring-blue-600'}`} />
      <p id={`${id}-count`} aria-live="polite" className={`flex justify-between gap-2 text-xs ${bad ? 'text-red-700' : 'text-slate-500'}`}>
        <span>{issue?.message ?? ''}</span>
        <span data-testid="review-count">{count}/{REVIEW_TEXT_MAX}</span>
      </p>
    </div>
  );
}

export function AskReviewDialog({ open, onClose, projectId, documentId, templateTitle }: {
  open: boolean; onClose: () => void; projectId: string; documentId: string; templateTitle: string;
}) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const state = reviewTextState(text);
  const send = useMutation({
    mutationFn: () => askReview(documentId, cleanReviewText(text)),
    onSuccess: () => {
      setText('');
      void qc.invalidateQueries({ queryKey: myReviewsKey(projectId) });
      onClose();
    },
  });
  return (
    <Modal open={open} title="Ask for review" onClose={onClose}>
      <form className="flex flex-col gap-3 text-sm" onSubmit={e => { e.preventDefault(); if (state.sendable) send.mutate(); }}>
        <p>
          Not sure how to fill "{templateTitle}"? Send the admin one short question. The admin answers here, or changes the
          template. It is not a chat: one question, one answer.
        </p>
        <ReviewTextBox label="What are you unsure about?" value={text} onChange={setText}
          hint="Do not type Aadhaar, PAN or TAN numbers or the client's details." />
        <ErrorBox error={send.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" tone="primary" disabled={!state.sendable || send.isPending}>Send</Button>
        </div>
      </form>
    </Modal>
  );
}
